import { Box, Dialog, DialogContent, DialogTitle } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import {
    List,
    Datagrid,
    TextField,
    TextInput,
    TopToolbar,
    SelectInput,
    Button,
    FunctionField,
    useDataProvider,
    useListContext,
    DateField,
    useNotify,
    SelectField
} from "react-admin";

const trafficStatusTypes = [
    { id: 0, name: '部署初始化' },
    { id: 1, name: '部署完成' },
    { id: 2, name: '部署失败' },
    { id: 3, name: '部署超时' },
    { id: 4, name: '未运行' },
    { id: 5, name: '运行中' },
    { id: 6, name: '运行失败' }
];

import { Terminal } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import { WebLinksAddon } from 'xterm-addon-web-links';
import 'xterm/css/xterm.css';

const ListActions = () => {
    const { setFilters, filterValues } = useListContext();  // 获取列表上下文
    const initialFilters = {};  // 初始过滤器值
    const dataProvider = useDataProvider(); // 获取数据提供者
    const notify = useNotify(); // 获取通知方法

    // 重置过滤器函数
    const handleResetFilters = () => {
        setFilters(initialFilters);
    };

    const exportDetails = () => {
        if (window.confirm(`确认导出设备列表？`)) {
            const formData = {
                ...filterValues,
            };
            dataProvider.deviceExport(formData)
                .then(() => {
                    notify('导出设备列表成功', { type: 'success' });
                })
                .catch((error: any) => {
                    notify(error.message || '导出设备列表失败', { type: 'error' });
                })
        }
    }

    return (
        <TopToolbar>
            <Button
                label="重置搜索"
                onClick={handleResetFilters}
                disabled={Object.keys(filterValues).length === 0}  // 当没有过滤条件时禁用
            />
            <Button
                label="导出"
                onClick={exportDetails}
            />
        </TopToolbar>
    );
};

// 统一按钮样式
const tableButtonStyle = {
    whiteSpace: 'nowrap',
    color: '#1976d2',
    '&:hover': {
        backgroundColor: 'rgba(25, 118, 210, 0.04)'
    }
};

// SSH终端组件
const SshTerminal = ({ config, open, onClose }: { config: any, open: boolean, onClose: () => void }) => {
    const terminalRef = useRef<HTMLDivElement>(null);
    const terminal = useRef<Terminal | null>(null);
    const fitAddon = useRef<FitAddon | null>(null);
    const socket = useRef<WebSocket | null>(null);
    const [isTerminalInitialized, setIsTerminalInitialized] = useState(false);

    // 初始化终端 - 使用 setTimeout 确保 DOM 已挂载
    useEffect(() => {
        if (open && !isTerminalInitialized) {
            const initTerminal = () => {
                if (!terminalRef.current) {
                    // 如果 ref 还是 null，延迟重试
                    setTimeout(initTerminal, 50);
                    return;
                }

                try {
                    // 初始化终端
                    terminal.current = new Terminal({
                        cursorBlink: true,
                        theme: {
                            background: '#1e1e1e',
                            foreground: '#ffffff'
                        },
                        rows: 30,
                        cols: 120
                    });

                    fitAddon.current = new FitAddon();
                    terminal.current.loadAddon(fitAddon.current);
                    terminal.current.loadAddon(new WebLinksAddon());

                    terminal.current.open(terminalRef.current!);
                    fitAddon.current.fit();

                    setIsTerminalInitialized(true);

                    // 终端初始化后再连接 WebSocket
                    if (config) {
                        connectWebSocket();
                    }
                } catch (error) {
                    console.error('初始化终端失败:', error);
                }
            };

            // 延迟初始化，确保 DOM 已渲染
            setTimeout(initTerminal, 100);
        }
    }, [open, isTerminalInitialized, config]);

    // 当 config 变化时重新连接
    useEffect(() => {
        if (isTerminalInitialized && config && terminal.current) {
            connectWebSocket();
        }
    }, [config, isTerminalInitialized]);

    // 处理窗口大小变化
    useEffect(() => {
        const handleResize = () => {
            if (fitAddon.current) {
                setTimeout(() => {
                    fitAddon.current?.fit();
                }, 100);
            }
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // 清理资源
    useEffect(() => {
        return () => {
            if (socket.current) {
                socket.current.close();
            }
            if (terminal.current) {
                terminal.current.dispose();
                terminal.current = null;
            }
            setIsTerminalInitialized(false);
        };
    }, []);

    const connectWebSocket = () => {
        if (!config || !terminal.current) {
            console.warn('终端未准备好或配置为空');
            return;
        }

        try {
            // 关闭现有连接
            if (socket.current) {
                socket.current.close();
            }

            const wsUrl = `ws://${config.serverAddr}:${config.proxies[0]?.remotePort || 8080}/ssh?sn=${config.sn}`;
            console.log('连接 WebSocket:', wsUrl);

            socket.current = new WebSocket(wsUrl);

            socket.current.onopen = () => {
                const authMessage = {
                    type: 'auth',
                    username: 'root',
                    password: 'admin'
                };

                socket.current.send(JSON.stringify(authMessage));

                terminal.current?.write('\r\n\x1b[32mSSH连接已建立\x1b[0m\r\n');
                terminal.current?.write('\x1b[33m$ \x1b[0m');
            };

            socket.current.onmessage = (event) => {
                terminal.current?.write(event.data);
            };

            socket.current.onclose = () => {
                terminal.current?.write('\r\n\x1b[31mSSH连接已关闭\x1b[0m\r\n');
            };

            socket.current.onerror = (error) => {
                console.error('WebSocket error:', error);
                terminal.current?.write('\r\n\x1b[31mSSH连接错误\x1b[0m\r\n');
            };

            // 终端输入处理
            terminal.current.onData((data) => {
                if (socket.current && socket.current.readyState === WebSocket.OPEN) {
                    socket.current.send(data);
                }
            });

        } catch (error) {
            console.error('连接SSH失败:', error);
            terminal.current?.write('\r\n\x1b[31m连接SSH失败，请检查网络连接\x1b[0m\r\n');
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="lg"
            sx={{ '& .MuiDialog-paper': { height: '80vh' } }}
            TransitionProps={{
                onEntered: () => {
                    // 弹窗打开动画完成后重新调整终端大小
                    setTimeout(() => {
                        fitAddon.current?.fit();
                    }, 300);
                }
            }}
        >
            <DialogTitle>
                SSH终端 - {config?.sn}
                <Box sx={{ fontSize: '0.8rem', color: 'text.secondary', mt: 0.5 }}>
                    服务器: {config?.serverAddr}:{config?.serverPort}
                </Box>
            </DialogTitle>
            <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
                <Box
                    ref={terminalRef}
                    sx={{
                        flex: 1,
                        backgroundColor: '#1e1e1e',
                        p: 1,
                        minHeight: '400px',
                        width: '100%'
                    }}
                />
                {!isTerminalInitialized && (
                    <Box sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        color: 'white'
                    }}>
                        初始化终端中...
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export const EquipmentList = () => {
    const dataProvider = useDataProvider(); // 获取dataProvider
    const [customerTypeChoices, setCustomerTypeChoices] = useState([]);
    const [trafficCustomers, setTrafficCustomers] = useState([]);
    const [deviceTypes, setDeviceTypes] = useState([]);
    const notify = useNotify();
    const [dialogOpen, setDialogOpen] = useState(false); // 重命名为dialogOpen避免命名冲突
    const [sshDialogOpen, setSshDialogOpen] = useState(false);
    const [sshConfig, setSshConfig] = useState<any>(null);

    useEffect(() => {
        // 异步获取客户类型数据
        const fetchCustomerTypes = async () => {
            try {
                const [salesData, trafficData, deviceData] = await Promise.all([
                    dataProvider.getSalesCustomers(),
                    dataProvider.getTrafficCustomers(),
                    dataProvider.getDeviceList()
                ]);
                setCustomerTypeChoices(salesData.map((item: any) => ({
                    id: item.id,
                    name: item.name
                })));
                setTrafficCustomers(trafficData.map((item: any) => ({
                    id: item.id,
                    name: item.name
                })));
                setDeviceTypes(deviceData.map((item: any) => ({
                    id: item.name,
                    name: item.name
                })))
            } catch (error) {
                console.error("获取下拉列表失败:", error);
            }
        };

        fetchCustomerTypes();
    }, []);  // 空依赖数组确保只调用一次

    // 常量提取，便于维护
    let FILTER_CONFIG = {
        MERCHANT_OPTIONS: customerTypeChoices,
        STATUS_OPTIONS: [
            { id: 1, name: '在线' },
            { id: 0, name: '离线' }
        ],

        ONLINE_OPTIONS: [
            { id: 1, name: '已激活' },
            { id: 0, name: '未激活' }
        ]
    };

    let postFilters = [
        <SelectInput
            alwaysOn
            key="saleCustomerIds"
            source="saleCustomerIds"
            label="设备商家"
            choices={FILTER_CONFIG.MERCHANT_OPTIONS}
        />,
        <SelectInput
            alwaysOn
            source="trafficCustomerIds"
            label="流量商家"
            choices={trafficCustomers}
        />,
        <TextInput
            alwaysOn
            key="sns"
            label="设备SN"
            source="sns"
        />,
        <SelectInput
            source="trafficStatusList"
            label="运行状态"
            choices={trafficStatusTypes}
            alwaysOn
        />,
        <SelectInput
            alwaysOn
            key="deviceStatus"
            source="deviceStatus"
            label="设备状态"
            choices={FILTER_CONFIG.STATUS_OPTIONS}
        />,
        <SelectInput
            alwaysOn
            key="activateStatus"
            source="activateStatus"
            label="设备激活"
            choices={FILTER_CONFIG.ONLINE_OPTIONS}
        />,
        <SelectInput
            label="设备类型"
            source="deviceType"
            choices={deviceTypes}
            alwaysOn
        />
    ];

    const handleOperation = (sn: any) => {
        // if (window.confirm(`确定执行故障诊断操作？`)) {
        //     // 调用API逻辑
        //     dataProvider.detectFault({ sn: sn, checkItems: [1, 2, 3, 4, 5, 6, 7, 8] }).then(res => {
        //         console.log(res)
        //         setDialogOpen(true)
        //     }).catch(err => {
        //         console.log(err)
        //         notify('诊断失败', { type: 'error' });
        //     })
        // }
        dataProvider.frpcConfig({ sn: 'PH00MD253600041' }).then((config: any) => {
            console.log('FRPC配置:', config);
            let orz = {
                "sn": "PH00MD253600041",   //设备sn 
                "status": 1,
                "serverAddr": "47.121.24.61",
                "serverPort": 10047,
                "method": "token",
                "token": "hua!@#.",
                "proxies": [
                    {
                        "name": "route-PH00MC252900218",
                        "type": "tcp",
                        "localIP": "127.0.0.1",
                        "localPort": 22,
                        "remotePort": 10042
                    },
                    {
                        "name": "pcdn-PH00MC2529002188",
                        "type": "tcp",
                        "localIP": "192.168.99.222",
                        "localPort": 22,
                        "remotePort": 10045
                    }
                ]
            }
            setSshConfig(orz);
            setSshDialogOpen(true);
        }).catch((err: any) => {
            console.log(err);
            notify('获取SSH配置失败', { type: 'error' });
        });
    };

    const restoreFactorySettings = async (sn: any) => {
        if (window.confirm(`确定执行复位操作？`)) {
            await dataProvider.restoreFactorySettings(sn).then(() => {
                notify('复位成功', { type: 'success' });
            })
                .catch((error: any) => {
                    notify(error.message || '复位失败', { type: 'error' });
                });
        }
    };

    const restartSettings = async (sn: any) => {
        if (window.confirm(`确定执行下发设备重启操作？`)) {
            await dataProvider.restartOps(sn).then(() => {
                notify('下发设备重启成功', { type: 'success' });
            })
                .catch((error: any) => {
                    notify(error.message || '下发设备重启失败', { type: 'error' });
                });
        }
    };

    return (
        <>
            <List filters={postFilters} actions={<ListActions />} sx={{
                "& .RaList-content": {
                    overflow: "auto",
                }
            }}>
                <Datagrid bulkActionButtons={false}>
                    {/* 修正字段映射 */}
                    <TextField source="id" label="序号" sortable={false} sx={{
                        whiteSpace: 'nowrap',
                    }} />
                    <TextField source="sn" label="设备SN" sortable={false} sx={{
                        whiteSpace: 'nowrap',
                    }} />

                    <FunctionField
                        label="累计流量"
                        source="totalTraffic"
                        render={record => {
                            // 确保值存在且为数字
                            const value = Number(record.totalTraffic);
                            if (isNaN(value)) return '0.000 TB';

                            // 将字节转换为TB并保留三位小数
                            const tb = (value / (1024 * 1024 * 1024 * 1024)).toFixed(3);
                            return `${tb} TB`;
                        }}
                        sortable={false}
                        sx={{
                            whiteSpace: 'nowrap',
                        }}
                    />
                    <TextField source="trafficCustomerName" label="流量商家" sortable={false} sx={{
                        whiteSpace: 'nowrap',
                    }} />

                    <TextField source="saleCustomerName" label="设备商家" sortable={false} sx={{
                        whiteSpace: 'nowrap',
                    }} />

                    <DateField source="activateTime" label="用户绑定日期" sortable={false} sx={{
                        whiteSpace: 'nowrap',
                    }} />
                    <FunctionField
                        label="在线时长"
                        source="totalOnlineDuration"
                        render={record => {
                            const totalSeconds = record.totalOnlineDuration;
                            const days = Math.floor(totalSeconds / 86400);
                            const hours = Math.floor((totalSeconds % 86400) / 3600);

                            if (days > 0) {
                                return `${days}天${hours}小时`;
                            } else {
                                return `${hours}小时`;
                            }
                        }}
                        sortable={false}
                        sx={{
                            whiteSpace: 'nowrap',
                        }}
                    />
                    <FunctionField
                        label="离线时间"
                        source="lastOfflineTime"
                        render={record => {
                            if (!record.lastOfflineTime) return '-';

                            const date = new Date(record.lastOfflineTime);

                            // 检查是否有时分信息
                            const hasTime = date.getHours() !== 0 || date.getMinutes() !== 0 ||
                                date.getSeconds() !== 0 || date.getMilliseconds() !== 0;

                            if (hasTime) {
                                // 显示到分钟：2025/10/24 10:05
                                const year = date.getFullYear();
                                const month = String(date.getMonth() + 1).padStart(2, '0');
                                const day = String(date.getDate()).padStart(2, '0');
                                const hours = String(date.getHours()).padStart(2, '0');
                                const minutes = String(date.getMinutes()).padStart(2, '0');

                                return `${year}/${month}/${day} ${hours}:${minutes}`;
                            } else {
                                // 显示到小时：2025/10/24 10时
                                const year = date.getFullYear();
                                const month = String(date.getMonth() + 1).padStart(2, '0');
                                const day = String(date.getDate()).padStart(2, '0');
                                const hours = String(date.getHours()).padStart(2, '0');

                                return `${year}/${month}/${day} ${hours}时`;
                            }
                        }}
                        sortable={false}
                        sx={{
                            whiteSpace: 'nowrap',
                        }}
                    />


                    <FunctionField
                        label="设备状态"
                        source="deviceStatus"
                        render={record => record.deviceStatus == 1 ? '在线' : '离线'}
                        sortable={false}
                        sx={{
                            whiteSpace: 'nowrap',
                        }}
                    />
                    <TextField source="province" label="设备位置" sortable={false} sx={{
                        whiteSpace: 'nowrap',
                    }} />

                    <TextField source="operator" label="运营商" sortable={false} sx={{
                        whiteSpace: 'nowrap',
                    }} />

                    <TextField source="natType" label="NAT类型" sortable={false} sx={{
                        whiteSpace: 'nowrap',
                    }} />
                    <FunctionField
                        label="上行网速"
                        source="uploadSpeed"
                        render={record => (record.uploadSpeed || 0) + 'Mbps'}
                        sortable={false}
                        sx={{
                            whiteSpace: 'nowrap',
                        }}
                    />
                    <TextField source="softwareVersion" label="软件版本" sortable={false} sx={{
                        whiteSpace: 'nowrap',
                    }} />
                    <TextField source="firmwareVersion" label="固件版本" sortable={false} sx={{
                        whiteSpace: 'nowrap',
                    }} />


                    <TextField source="internetMode" label="上网模式" sortable={false} sx={{
                        whiteSpace: 'nowrap',
                    }} />
                    <TextField source="imei" label="IMEI" sortable={false} sx={{
                        whiteSpace: 'nowrap',
                    }} />

                    <SelectField source="trafficStatus" label="运行状态" choices={trafficStatusTypes} sortable={false} sx={{
                        whiteSpace: 'nowrap',
                    }} />

                    <TextField source="deviceTag" label="带标签" sortable={false} sx={{
                        whiteSpace: 'nowrap',
                    }} />

                    {/* 操作按钮 */}
                    <FunctionField
                        label="操作"
                        render={(record) => (
                            <Box sx={{ justifyContent: 'center', display: 'flex', gap: '8px' }}>
                                <Button
                                    label="故障诊断"
                                    onClick={() => handleOperation(record.sn)}
                                    sx={tableButtonStyle}
                                />
                                <Button
                                    label="复位"
                                    onClick={() => restoreFactorySettings(record.sn)}
                                    sx={tableButtonStyle}
                                />
                                <Button
                                    label="重启"
                                    onClick={() => restartSettings(record.sn)}
                                    sx={tableButtonStyle}
                                />
                            </Box>
                        )}
                        sortable={false}
                    />
                </Datagrid>
            </List>
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>诊断结果</DialogTitle>
                <DialogContent>

                </DialogContent>
            </Dialog>

            {/* SSH终端弹窗 */}
            <SshTerminal
                config={sshConfig}
                open={sshDialogOpen}
                onClose={() => setSshDialogOpen(false)}
            />
        </>
    );
};